import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, CreditCard, Banknote, Clock, MapPin, Phone, Mail, Car, X, ChevronLeft, ChevronRight, Search, Filter, TrendingUp, Users, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const STATUS_LABELS = {
  paid: { label: 'Payé', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  cash_pending: { label: 'Espèces', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  pending: { label: 'En attente', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  cancelled: { label: 'Annulé', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || { label: status, cls: 'bg-white/10 text-white/50' };
  return <span className={`text-xs px-2 py-1 border ${s.cls}`} data-testid={`status-badge-${status}`}>{s.label}</span>;
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-[#C5A059]' }) => (
  <div className="bg-[#0a0a0a] border border-white/10 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <Icon size={18} className="text-white/30" />
      <span className={`text-2xl font-light ${color}`}>{value}</span>
    </div>
    <p className="text-white/50 text-xs uppercase tracking-wider">{label}</p>
    {sub && <p className="text-white/30 text-xs mt-1">{sub}</p>}
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const res = await fetch(url, { ...options, headers: { ...getAuthHeaders(), ...options.headers }, credentials: 'include' });
    if (res.status === 401 || res.status === 403) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
      return null;
    }
    return res;
  }, [navigate]);

  const loadStats = useCallback(async () => {
    const res = await fetchWithAuth(`${API_URL}/api/admin/stats`);
    if (res?.ok) setStats(await res.json());
  }, [fetchWithAuth]);

  const loadBookings = useCallback(async () => {
    const params = new URLSearchParams({ page: page.toString(), limit: '15' });
    if (statusFilter !== 'all') params.append('status', statusFilter);
    const res = await fetchWithAuth(`${API_URL}/api/admin/bookings?${params}`);
    if (res?.ok) {
      const data = await res.json();
      setBookings(data.bookings);
      setTotal(data.total);
      setPages(data.pages);
    }
  }, [fetchWithAuth, page, statusFilter]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    Promise.all([loadStats(), loadBookings()]).then(() => setLoading(false));
  }, [loadStats, loadBookings, navigate]);

  const handleStatusChange = useCallback(async (bookingId, newStatus) => {
    const res = await fetchWithAuth(`${API_URL}/api/admin/bookings/${bookingId}`, {
      method: 'PATCH',
      body: JSON.stringify({ payment_status: newStatus })
    });
    if (res?.ok) {
      loadBookings();
      loadStats();
      setSelectedBooking(null);
    }
  }, [fetchWithAuth, loadBookings, loadStats]);

  const handleCancel = useCallback(async (bookingId) => {
    if (!window.confirm('Confirmer l\'annulation ?')) return;
    const res = await fetchWithAuth(`${API_URL}/api/admin/bookings/${bookingId}`, { method: 'DELETE' });
    if (res?.ok) {
      loadBookings();
      loadStats();
      setSelectedBooking(null);
    }
  }, [fetchWithAuth, loadBookings, loadStats]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  }, [navigate]);

  const filteredBookings = searchTerm
    ? bookings.filter(b => b.name?.toLowerCase().includes(searchTerm.toLowerCase()) || b.phone?.includes(searchTerm) || b.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    : bookings;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#C5A059] animate-pulse text-lg">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <h1 className="text-[#C5A059] text-lg font-light tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            INTERFLEX <span className="text-white/30 text-sm">Admin</span>
          </h1>
        </div>
        <button onClick={handleLogout} className="text-white/40 hover:text-white flex items-center gap-2 text-sm transition-colors" data-testid="admin-logout-btn">
          <LogOut size={16} />Déconnexion
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
            <StatCard icon={TrendingUp} label="Chiffre d'affaires" value={`${stats.total_revenue.toLocaleString('fr-FR')}€`} sub={`${stats.total_deposits.toLocaleString('fr-FR')}€ d'acomptes`} />
            <StatCard icon={Users} label="Réservations" value={stats.total_bookings} sub={`${stats.today_count} aujourd'hui`} />
            <StatCard icon={DollarSign} label="Payées" value={stats.paid_bookings} color="text-green-400" sub={`${stats.cash_bookings} en espèces`} />
            <StatCard icon={Clock} label="En attente" value={stats.pending_bookings} color="text-blue-400" sub={`${stats.cancelled_bookings} annulée(s)`} />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom, téléphone, email..."
              className="w-full bg-[#0a0a0a] border border-white/10 text-white pl-10 pr-4 py-2.5 text-sm focus:border-[#C5A059] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-white/30" />
            {['all', 'paid', 'cash_pending', 'pending', 'cancelled'].map(s => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`text-xs px-3 py-2 border transition-colors ${statusFilter === s ? 'border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10' : 'border-white/10 text-white/40 hover:text-white/60'}`}
                data-testid={`filter-${s}`}
              >
                {s === 'all' ? 'Tous' : STATUS_LABELS[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden" data-testid="bookings-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider hidden md:table-cell">Trajet</th>
                  <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider">Prix</th>
                  <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider">Statut</th>
                  <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(b => (
                  <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-white text-sm font-medium">{b.name}</div>
                      <div className="text-white/40 text-xs flex items-center gap-1"><Phone size={10} />{b.phone}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-white/60 text-xs truncate max-w-[200px]">{b.pickup_address}</div>
                      <div className="text-white/40 text-xs truncate max-w-[200px]">→ {b.dropoff_address}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white text-sm">{b.pickup_date}</div>
                      <div className="text-white/40 text-xs">{b.pickup_time}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#C5A059] text-sm font-medium">{b.total_price}€</div>
                      <div className="text-white/30 text-xs">{b.deposit_amount}€ acompte</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={b.payment_status} /></td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedBooking(b)}
                        className="text-[#C5A059] text-xs hover:underline"
                        data-testid={`view-booking-${b.id}`}
                      >
                        Détails
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredBookings.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-white/30 text-sm">Aucune réservation trouvée</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <span className="text-white/30 text-xs">{total} réservation(s)</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-white/40 hover:text-white disabled:opacity-30 p-1" data-testid="prev-page">
                  <ChevronLeft size={16} />
                </button>
                <span className="text-white/50 text-xs">{page} / {pages}</span>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="text-white/40 hover:text-white disabled:opacity-30 p-1" data-testid="next-page">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" data-testid="booking-detail-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="text-white text-sm font-medium">Détails réservation</h3>
              <button onClick={() => setSelectedBooking(null)} className="text-white/40 hover:text-white" data-testid="close-detail-modal"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1">Client</p>
                  <p className="text-white text-sm font-medium">{selectedBooking.name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1">Téléphone</p>
                  <a href={`tel:${selectedBooking.phone}`} className="text-[#C5A059] text-sm">{selectedBooking.phone}</a>
                </div>
              </div>
              {selectedBooking.email && (
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1 flex items-center gap-1"><Mail size={10} />Email</p>
                  <p className="text-white text-sm">{selectedBooking.email}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1 flex items-center gap-1"><Calendar size={10} />Date</p>
                  <p className="text-white text-sm">{selectedBooking.pickup_date} à {selectedBooking.pickup_time}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1 flex items-center gap-1"><Car size={10} />Véhicule</p>
                  <p className="text-white text-sm">{selectedBooking.vehicle === 'model_y' ? 'Tesla Model Y' : 'Tesla Model 3'}</p>
                </div>
              </div>
              <div>
                <p className="text-white/40 text-xs uppercase mb-1 flex items-center gap-1"><MapPin size={10} />Départ</p>
                <p className="text-white text-sm">{selectedBooking.pickup_address}</p>
              </div>
              <div>
                <p className="text-white/40 text-xs uppercase mb-1 flex items-center gap-1"><MapPin size={10} />Arrivée</p>
                <p className="text-white text-sm">{selectedBooking.dropoff_address}</p>
              </div>
              {selectedBooking.distance_km && (
                <p className="text-white/40 text-xs">Distance: {selectedBooking.distance_km} km</p>
              )}
              <div className="border-t border-white/10 pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-white/50 text-sm">Prix total</span>
                  <span className="text-[#C5A059] text-lg font-medium">{selectedBooking.total_price}€</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/30 text-xs">Acompte (50%)</span>
                  <span className="text-white/50 text-sm">{selectedBooking.deposit_amount}€</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/30 text-xs">Paiement</span>
                  <div className="flex items-center gap-2">
                    {selectedBooking.payment_method === 'card' ? <CreditCard size={14} className="text-white/30" /> : <Banknote size={14} className="text-white/30" />}
                    <StatusBadge status={selectedBooking.payment_status} />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <p className="text-white/40 text-xs uppercase mb-2">Changer le statut</p>
                <div className="flex flex-wrap gap-2">
                  {selectedBooking.payment_status !== 'paid' && (
                    <button onClick={() => handleStatusChange(selectedBooking.id, 'paid')} className="text-xs px-3 py-1.5 bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 transition-colors" data-testid="mark-paid-btn">
                      Marquer payé
                    </button>
                  )}
                  {selectedBooking.payment_status !== 'cash_pending' && (
                    <button onClick={() => handleStatusChange(selectedBooking.id, 'cash_pending')} className="text-xs px-3 py-1.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20 transition-colors" data-testid="mark-cash-btn">
                      Espèces
                    </button>
                  )}
                  {selectedBooking.payment_status !== 'cancelled' && (
                    <button onClick={() => handleCancel(selectedBooking.id)} className="text-xs px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors" data-testid="cancel-booking-btn">
                      Annuler
                    </button>
                  )}
                </div>
                <a href={`tel:${selectedBooking.phone}`} className="block text-center text-xs px-3 py-2 bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/20 hover:bg-[#C5A059]/20 transition-colors mt-3" data-testid="call-client-btn">
                  <Phone size={12} className="inline mr-1" />Appeler le client
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
